import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PersonalBookUploadForm } from './PersonalBookUploadForm';
import { 
  Building2,
  Scissors,
  Tag,
  RefreshCcw,
  Check,
  ChevronsUpDown,
  Clock,
  Sparkles,
  Layers,
  Globe as GlobeIcon,
  Settings,
  ArrowLeft, 
  FileText, 
  Plus, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  GripVertical,
  LayoutList,
  Save,
  PlusCircle,
  Info,
  Edit3,
  ExternalLink,
  ChevronUp,
  X,
  Wind,
  BookOpen,
  Eye,
  CheckCircle2,
  AlertCircle,
  Upload,
  Loader2,
  Download,
  Folder
} from 'lucide-react';
import { toast } from "sonner";
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { db, auth, storage } from '../../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, getDocs, query, orderBy, onSnapshot, addDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { subjectCategories } from './subjectCategories';

// ═══════════════════════════════════════════
// LOCATION-STATE PERSISTENCE
// A page reload wipes React Router's location.state. Without a fallback the
// editor would lose the `subjectId` / `isFirebaseSubject` it was working on and
// fall back to a hard-coded mock curriculum. Persist the navigation context
// (and a couple of helper fields) to sessionStorage so a refresh — or a
// back-button that re-enters this route — keeps the same subject selected.
// ═══════════════════════════════════════════
const OUTLINE_EDITOR_STATE_KEY = 'vochak_outline_editor_state';

const readPersistedEditorState = (): Record<string, any> | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(OUTLINE_EDITOR_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const persistableEditorStateFields = [
  'subjectId',
  'subjectName',
  'isFirebaseSubject'
] as const;

interface SubTopic {
  id: string;
  title: string;
}

interface Topic {
  id: string;
  title: string;
  isExpanded: boolean;
  subTopics: SubTopic[];
}

const CourseOutlineEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Pull context from location.state, but fall back to the last persisted
  // editor state on a hard refresh (location.state is null after refresh).
  const incomingState = location.state || readPersistedEditorState() || null;
  const { subjectId, isFirebaseSubject, readOnly = false } = incomingState || { subjectId: null, isFirebaseSubject: false, readOnly: false };
  const [courseTitle, setCourseTitle] = useState((incomingState as any)?.subjectName || '');

  const [outlineContent, setOutlineContent] = useState<string | null>(null);
  const [showFullOutline, setShowFullOutline] = useState(false);
  const [mainPdfUrl, setMainPdfUrl] = useState<string | null>(null);
  const [subContentMap, setSubContentMap] = useState<Record<string, any>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [subjectResources, setSubjectResources] = useState<any[]>([]);

  // Upload-resource popup state — wraps PersonalBookUploadForm (auto-fills subject from the course doc)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
 // keyed by `{topicId}_{subTopicId}`
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingSubject, setIsDeletingSubject] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: location.state?.subjectName || '',
    category: '',
    isMultiSchool: 'no',
    isContinuation: false,
    continuesFrom: '',
    customCategory: '',
    approxHours: '',
    // richly-typed: each school carries its own class list
    schoolsData: [{ name: '', classes: [''] }] as { name: string; classes: string[] }[]
  });  
  // track which school's class-input is open (index)
  const [addingClassForSchool, setAddingClassForSchool] = useState<number | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [openCategory, setOpenCategory] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [existingSubjects, setExistingSubjects] = useState<{value: string, label: string}[]>([]);
  
  // Topics are always hydrated from Firestore. We intentionally do NOT seed
  // mock placeholder topics here — a hard refresh that loses `location.state`
  // would otherwise show "Foundations of Programming" / "Data Structures &
  // Algorithms" for a moment before the real subject loads. The `isLoading`
  // flag below keeps the content area covered with a loader until the real
  // data is available.
  const [topics, setTopics] = useState<Topic[]>([]);
  const [isLoadingSyllabus, setIsLoadingSyllabus] = useState<boolean>(true);

  const [newTopicTitle, setNewTopicTitle] = useState("");
  const [subTopicTitle, setSubTopicTitle] = useState("");
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);

  // Inline rename state
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTopicValue, setEditingTopicValue] = useState('');
  const [editingSubKey, setEditingSubKey] = useState<string | null>(null);
  const [editingSubValue, setEditingSubValue] = useState('');

  useEffect(() => {
    if (!subjectId || !auth.currentUser) return;
    
    const uid = auth.currentUser.uid;
    const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);

    // Fetch syllabus main data and all contents
    const fetchSyllabus = async () => {
      try {
        const mainRef = doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`, 'main');
        const snap = await getDoc(mainRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.outlineContent) setOutlineContent(data.outlineContent);
          if (data.referencePdfUrl) setMainPdfUrl(data.referencePdfUrl);
          if (data.topics && Array.isArray(data.topics) && data.topics.length > 0) {
            setTopics(data.topics);
          }
        }

        const syllabusCol = collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`);
        const allDocs = await getDocs(syllabusCol);
        const contentMap: Record<string, any> = {};
        allDocs.forEach(docSnap => {
          const id = docSnap.id;
          if (id.startsWith('content_')) {
            const d = docSnap.data();
            const mapKey = id.replace('content_', '');
            // Count items (newer multi-content structure) and fall back to
            // legacy single-content fields so old data still works.
            const itemsArr = Array.isArray(d.items) ? d.items : [];
            const itemsCount = itemsArr.length;
            const hasPdf = itemsArr.some((i: any) => i?.type === 'pdf' && i?.url) || !!d.referencePdfUrl;
            const hasOutline = itemsArr.some((i: any) => i?.type === 'outline' && i?.content) || !!d.outlineContent;
            contentMap[mapKey] = {
              ...d,
              items: itemsArr,
              itemsCount,
              pdfCount: itemsArr.filter((i: any) => i?.type === 'pdf').length,
              outlineCount: itemsArr.filter((i: any) => i?.type === 'outline').length,
              hasPdf,
              hasOutline,
            };
          }
        });
        setSubContentMap(contentMap);
      } catch (err) {
        console.error("Error fetching syllabus:", err);
      } finally {
        setIsLoadingSyllabus(false);
      }
    };
    
    fetchSyllabus();

    // REAL-TIME listener for course details (pre-filling the edit form)
    const unsubscribeDetails = onSnapshot(courseRef, (docSnap) => {
      if (docSnap.exists()) {
        const cData = docSnap.data();
        setCourseTitle(cData.title);
        // Migrate legacy flat-schools array → schoolsData objects
        let loadedSchoolsData: { name: string; classes: string[] }[] = [{ name: '', classes: [''] }];
        if (Array.isArray(cData.schoolsData) && cData.schoolsData.length > 0) {
          loadedSchoolsData = cData.schoolsData.map((s: any) => ({
            name: s.name || '',
            classes: Array.isArray(s.classes) && s.classes.length > 0 ? s.classes : ['']
          }));
        } else if (Array.isArray(cData.schools) && cData.schools.length > 0) {
          // legacy migration: schools was a string[]
          loadedSchoolsData = cData.schools.map((name: string) => ({ name, classes: [''] }));
        }
        setEditFormData({
          title: cData.title || '',
          category: cData.category || '',
          isMultiSchool: cData.isMultiSchool ? 'yes' : 'no',
          isContinuation: cData.isContinuation || false,
          continuesFrom: cData.continuesFrom || '',
          customCategory: cData.customCategory || '',
          approxHours: cData.approxHours || '',
          schoolsData: loadedSchoolsData
        });
      }
    });

    // Setup listener for continuation subjects search
    const q = query(collection(db, `teaching_workspaces/${uid}/courses`), orderBy('createdAt', 'desc'));
    const unsubscribeList = onSnapshot(q, (snapshot) => {
      const fetched = snapshot.docs.map(ds => {
        const data = ds.data();
        return { value: ds.id, label: data.title || ds.id };
      });
      setExistingSubjects(fetched);
    });

    return () => {
      unsubscribeDetails();
      unsubscribeList();
    };
  }, [subjectId, auth.currentUser]);

  // Persist the navigation context (subjectId, isFirebaseSubject, etc.) so a
  // hard refresh — which wipes React Router's location.state — still lands
  // the user on the same course outline. Skips when subjectId is missing so
  // we never pin a blank session.
  useEffect(() => {
    if (!location.state) return;
    const incoming = location.state as Record<string, any>;
    if (!incoming.subjectId) return;
    try {
      const persistable: Record<string, any> = {};
      persistableEditorStateFields.forEach((key) => {
        if (incoming[key] !== undefined) persistable[key] = incoming[key];
      });
      sessionStorage.setItem(OUTLINE_EDITOR_STATE_KEY, JSON.stringify(persistable));
    } catch (e) {
      console.error('Failed to persist editor state', e);
    }
  }, [location.state]);

  // If there's no subject to load (e.g. user deep-linked into this URL with
  // no location.state AND nothing in sessionStorage), stop the loader from
  // spinning forever. The render below shows a "go pick a subject" CTA.
  useEffect(() => {
    if (!subjectId || !auth.currentUser) setIsLoadingSyllabus(false);
  }, [subjectId, auth.currentUser]);

  // Load resources from Firestore
  useEffect(() => {
    if (!subjectId || !auth.currentUser) {
      setSubjectResources([]);
      return;
    }

    const uid = auth.currentUser.uid;
    const resourcesRef = collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/resources`);
    
    const unsubscribe = onSnapshot(resourcesRef, (snapshot) => {
      const resources = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSubjectResources(resources);
    }, (error) => {
      console.error("Error loading resources:", error);
    });

    return () => unsubscribe();
  }, [subjectId, auth.currentUser]);

  const handleSaveSubjectDetails = async () => {
    if (!subjectId || !auth.currentUser) return;
    if (!editFormData.title.trim() || !editFormData.category) {
      toast.error('Title and Category are required.');
      return;
    }

    setIsSavingDetails(true);
    try {
      const uid = auth.currentUser.uid;
      const ref = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);
      // Build clean schoolsData — drop empty school names, drop empty class names
      const cleanSchoolsData = editFormData.isMultiSchool === 'yes'
        ? editFormData.schoolsData
            .filter(s => s.name.trim() !== '')
            .map(s => ({
              name: s.name.trim(),
              classes: s.classes.filter(c => c.trim() !== '')
            }))
        : [];
      // Derive flat schools[] for backward-compat with LessonPlannerView selector
      const flatSchools = cleanSchoolsData.map(s => s.name);
      await updateDoc(ref, {
        title: editFormData.title.trim(),
        category: editFormData.category,
        customCategory: editFormData.customCategory,
        approxHours: editFormData.approxHours,
        isMultiSchool: editFormData.isMultiSchool === 'yes',
        schoolsData: cleanSchoolsData,
        schools: flatSchools,
        isContinuation: editFormData.isContinuation,
        continuesFrom: editFormData.isContinuation ? editFormData.continuesFrom : '',
        updatedAt: serverTimestamp(),
      });
      setCourseTitle(editFormData.title.trim());
      setIsEditDialogOpen(false);
      toast.success("Course details updated successfully!");
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to update course details.");
    } finally {
      setIsSavingDetails(false);
    }
  };

  const handleSchoolNameChange = (index: number, value: string) => {
    const updated = editFormData.schoolsData.map((s, i) => i === index ? { ...s, name: value } : s);
    setEditFormData({ ...editFormData, schoolsData: updated });
  };

  const handleAddSchoolEdit = () => {
    setEditFormData({ ...editFormData, schoolsData: [...editFormData.schoolsData, { name: '', classes: [''] }] });
  };

  const handleRemoveSchoolEdit = (index: number) => {
    const remaining = editFormData.schoolsData.filter((_, i) => i !== index);
    setEditFormData({ ...editFormData, schoolsData: remaining.length ? remaining : [{ name: '', classes: [''] }] });
  };

  const handleAddClassToSchool = (schoolIndex: number) => {
    if (!newClassName.trim()) return;
    const updated = editFormData.schoolsData.map((s, i) => {
      if (i !== schoolIndex) return s;
      const existingClasses = s.classes.filter(c => c.trim() !== '');
      if (existingClasses.includes(newClassName.trim())) {
        toast.error('Class already exists for this school');
        return s;
      }
      return { ...s, classes: [...existingClasses, newClassName.trim()] };
    });
    setEditFormData({ ...editFormData, schoolsData: updated });
    setNewClassName('');
    setAddingClassForSchool(null);
  };

  const handleRemoveClassFromSchool = (schoolIndex: number, classIndex: number) => {
    const updated = editFormData.schoolsData.map((s, i) => {
      if (i !== schoolIndex) return s;
      const newClasses = s.classes.filter((_, ci) => ci !== classIndex);
      return { ...s, classes: newClasses };
    });
    setEditFormData({ ...editFormData, schoolsData: updated });
  };

  const saveSyllabus = async (topicsOverride?: Topic[]) => {
    const topicsToSave = topicsOverride || topics;
    if (!subjectId || !auth.currentUser || !isFirebaseSubject) {
      if (!topicsOverride) toast.info("This subject isn't linked to a saved course yet. Open it from your lesson plans to start saving.");
      return;
    }
    try {
      const uid = auth.currentUser.uid;
      const ref = doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`, 'main');
      await setDoc(ref, { 
        topics: topicsToSave,
        lastUpdated: serverTimestamp()
      }, { merge: true });
      if (!topicsOverride) toast.success("Syllabus synced correctly");
    } catch (err) {
      console.error(err);
      if (!topicsOverride) toast.error("Failed to sync syllabus");
    }
  };

  const migrateTopicContentToFirstSubTopic = async (topicId: string, subTopicId: string) => {
    if (!subjectId || !auth.currentUser || !isFirebaseSubject) return;

    try {
      const uid = auth.currentUser.uid;
      const syllabusPath = `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`;
      const topicContentRef = doc(db, syllabusPath, `content_${topicId}_main`);
      const subTopicContentRef = doc(db, syllabusPath, `content_${topicId}_${subTopicId}`);
      const topicContentSnap = await getDoc(topicContentRef);

      if (!topicContentSnap.exists()) return;

      const topicContent = topicContentSnap.data();
      const hasTopicContent = Boolean(topicContent.referencePdfUrl || topicContent.outlineContent);
      if (!hasTopicContent) return;

      const subTopicContentSnap = await getDoc(subTopicContentRef);
      const subTopicContent = subTopicContentSnap.exists() ? subTopicContentSnap.data() : null;
      const subTopicAlreadyHasContent = Boolean(subTopicContent?.referencePdfUrl || subTopicContent?.outlineContent);

      if (!subTopicAlreadyHasContent) {
        await setDoc(subTopicContentRef, {
          ...topicContent,
          migratedFromTopicId: topicId,
          migratedAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
        }, { merge: true });

        setSubContentMap(prev => {
          const next = { ...prev };
          delete next[`${topicId}_main`];
          next[`${topicId}_${subTopicId}`] = {
            ...topicContent,
            hasPdf: !!topicContent.referencePdfUrl,
            hasOutline: !!topicContent.outlineContent,
          };
          return next;
        });
      }

      await deleteDoc(topicContentRef);
    } catch (err) {
      console.error("Failed to move topic content to first sub-topic:", err);
      toast.error("Sub-topic added, but existing topic content could not be moved automatically.");
    }
  };

  const addTopic = () => {
    if (!newTopicTitle.trim()) return;
    const newTopic: Topic = {
      id: Date.now().toString(),
      title: newTopicTitle,
      isExpanded: true,
      subTopics: []
    };
    const newTopics = [...topics, newTopic];
    setTopics(newTopics);
    saveSyllabus(newTopics);
    setNewTopicTitle("");
    toast.success("New topic added to syllabus");
  };

  const openSubTopicDialog = (topicId: string) => {
    setActiveTopicId(topicId);
    setSubTopicTitle("");
  };

  const addSubTopic = async () => {
    if (!subTopicTitle.trim() || !activeTopicId) return;
    const newSubTopicId = Date.now().toString();
    const activeTopic = topics.find(t => t.id === activeTopicId);
    const isFirstSubTopic = (activeTopic?.subTopics.length ?? 0) === 0;
    
    const newTopics = topics.map(t => {
      if (t.id === activeTopicId) {
        return {
          ...t,
          subTopics: [...t.subTopics, { id: newSubTopicId, title: subTopicTitle }]
        };
      }
      return t;
    });

    setTopics(newTopics);
    await saveSyllabus(newTopics);
    if (isFirstSubTopic) {
      await migrateTopicContentToFirstSubTopic(activeTopicId, newSubTopicId);
    }
    setActiveTopicId(null);
    setSubTopicTitle("");
    toast.success("Sub-topic added to module");
  };

  const removeTopic = (id: string) => {
    const newTopics = topics.filter(t => t.id !== id);
    setTopics(newTopics);
    saveSyllabus(newTopics);
  };

  const removeSubTopic = (topicId: string, subTopicId: string) => {
    const newTopics = topics.map(t => {
      if (t.id === topicId) {
        return {
          ...t,
          subTopics: t.subTopics.filter(st => st.id !== subTopicId)
        };
      }
      return t;
    });
    setTopics(newTopics);
    saveSyllabus(newTopics);
  };

  const toggleTopic = (id: string) => {
    const newTopics = topics.map(t => t.id === id ? { ...t, isExpanded: !t.isExpanded } : t);
    setTopics(newTopics);
    saveSyllabus(newTopics);
  };

  const saveTopicRename = (topicId: string, newTitle: string) => {
    if (!newTitle.trim()) { setEditingTopicId(null); return; }
    const newTopics = topics.map(t => t.id === topicId ? { ...t, title: newTitle.trim() } : t);
    setTopics(newTopics);
    saveSyllabus(newTopics);
    setEditingTopicId(null);
    toast.success('Topic renamed');
  };

  const saveSubTopicRename = (topicId: string, subTopicId: string, newTitle: string) => {
    if (!newTitle.trim()) { setEditingSubKey(null); return; }
    const newTopics = topics.map(t => {
      if (t.id !== topicId) return t;
      return { ...t, subTopics: t.subTopics.map(st => st.id === subTopicId ? { ...st, title: newTitle.trim() } : st) };
    });
    setTopics(newTopics);
    saveSyllabus(newTopics);
    setEditingSubKey(null);
    toast.success('Sub-topic renamed');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser || !subjectId) return;

    setIsUploading(true);
    try {
      const uid = auth.currentUser.uid;
      const fileRef = ref(storage, `teaching_workspaces/${uid}/courses/${subjectId}/resources/${Date.now()}_${file.name.replace(/\s+/g, '_')}`);
      const uploadTask = await uploadBytesResumable(fileRef, file);
      const downloadURL = await getDownloadURL(uploadTask.ref);

      await addDoc(collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/resources`), {
        name: file.name,
        url: downloadURL,
        size: file.size,
        type: file.type,
        createdAt: serverTimestamp()
      });

      const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);
      await updateDoc(courseRef, { resourcesUpdatedAt: serverTimestamp() });

      toast.success("Resource uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload resource");
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  // BookUploadForm calls onSubmit AFTER it has uploaded the file to `global_resources/`
  // and written the global_resources doc. We mirror it into the per-course resources
  // collection so the existing onSnapshot list below updates automatically.
  // (replaced by PersonalBookUploadForm which writes directly to the per-course collection)
  const handleDeleteResource = async (resourceId: string) => {
    if (!auth.currentUser || !subjectId) return;
    try {
      const uid = auth.currentUser.uid;
      
      await deleteDoc(doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/resources`, resourceId));
      const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);
      await updateDoc(courseRef, { resourcesUpdatedAt: serverTimestamp() });

      setSubjectResources(prev => prev.filter(r => r.id !== resourceId));
      toast.success("Resource deleted successfully");
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete resource");
    }
  };

  const handleDeleteSubject = async () => {
    if (!auth.currentUser || !subjectId) return;
    setIsDeletingSubject(true);
    const uid = auth.currentUser.uid;
    const basePath = `teaching_workspaces/${uid}/courses/${subjectId}`;

    const deleteCollectionDocs = async (path: string) => {
      try {
        const colRef = collection(db, path);
        const snap = await getDocs(colRef);
        const deletes = snap.docs.map((d) => deleteDoc(d.ref));
        await Promise.all(deletes);
      } catch (err) {
        console.error(`Error deleting collection at ${path}:`, err);
      }
    };

    const deleteStorageFolder = async (folderPath: string) => {
      try {
        const folderRef = ref(storage, folderPath);
        const list = await listAll(folderRef);
        const fileDeletes = list.items.map((item) => deleteObject(item).catch(() => null));
        const folderDeletes = list.prefixes.map((prefix) => deleteStorageFolder(prefix.fullPath));
        await Promise.all([...fileDeletes, ...folderDeletes]);
      } catch (err) {
        // Folder may not exist — that's fine
      }
    };

    try {
      // Delete all known subcollections
      await Promise.all([
        deleteCollectionDocs(`${basePath}/syllabus_data`),
        deleteCollectionDocs(`${basePath}/resources`),
        deleteCollectionDocs(`${basePath}/reading_progress`),
        deleteCollectionDocs(`${basePath}/bookmarks`),
        deleteCollectionDocs(`${basePath}/notes`),
      ]);

      // Delete storage folders (resources + pdfs)
      await Promise.all([
        deleteStorageFolder(`${basePath}/resources`),
        deleteStorageFolder(`${basePath}/pdfs`),
      ]);

      // Finally, delete the main course document
      await deleteDoc(doc(db, `teaching_workspaces/${uid}/courses`, subjectId));

      toast.success("Subject and all its materials deleted");
      navigate(-1);
    } catch (err) {
      console.error("Error deleting subject:", err);
      toast.error("Failed to delete subject");
    } finally {
      setIsDeletingSubject(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDownloadResource = async (url: string, title: string) => {
    try {
      toast.info(`Downloading ${title}...`);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const cleanUrl = url.split('?')[0];
      const fileExtension = cleanUrl.split('.').pop()?.toLowerCase() || 'pdf';
      link.download = `${title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Download Failed', { description: 'Unable to download the resource. Please try again.' });
    }
  };

  // Smart back: if the user reloaded the page directly into this URL, the
  // browser history is empty and `navigate(-1)` would dump them somewhere
  // random (or feel like a full page reload into a different site). Fall back
  // to the lesson plans dashboard in that case so the back button is always
  // a predictable client-side navigation.
  const handleBack = () => {
    const canGoBack = typeof window !== 'undefined'
      && window.history.state
      && typeof (window.history.state as any).idx === 'number'
      && (window.history.state as any).idx > 0;
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate('/dashboard/lessons', { replace: true });
    }
  };

  return (
    <div className="w-full font-sans pb-20">
      <div className="max-w-5xl mx-auto px-3 sm:px-0 space-y-8">

        {/* HEADER */}
        <header className="flex flex-wrap items-start justify-between gap-4 pt-4">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm"
              aria-label="Back to lesson plans"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Stage 4</span>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">{courseTitle}</h1>
              </div>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-2">
                <LayoutList className="h-3.5 w-3.5" /> Syllabus Management
              </p>
            </div>
            
 
          </div>
          
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto pt-1">
             <Button variant="outline" onClick={() => setIsEditDialogOpen(true)} className="h-9 rounded-lg font-bold border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-all text-xs flex-1 sm:flex-none">
                <Settings className="h-3.5 w-3.5 mr-2" /> Edit Details
             </Button>
             <Button 
               variant="outline" 
               onClick={() => setIsDeleteDialogOpen(true)}
               className="h-9 rounded-lg font-bold border-rose-200 dark:border-rose-900/50 bg-white dark:bg-slate-900 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:border-rose-300 shadow-sm transition-all text-xs flex-1 sm:flex-none"
             >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Subject
             </Button>
          </div>
        </header>

        {/* ═══ CONTENT-AREA LOADER ═══ */}
        {isLoadingSyllabus && (
          <div
            role="status"
            aria-live="polite"
            aria-label="Loading course content"
            className="min-h-[60vh] flex flex-col items-center justify-center gap-5 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm"
          >
            <div className="relative h-14 w-14">
              <div className="absolute inset-0 rounded-full border-2 border-indigo-200 dark:border-indigo-900/40" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-500 dark:border-t-indigo-400 animate-spin" />
              <FileText className="absolute inset-0 m-auto h-5 w-5 text-indigo-500" />
            </div>
            <div className="text-center space-y-1.5">
              <p className="text-sm font-bold text-slate-900 dark:text-white">Restoring your course…</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs">
                {courseTitle
                  ? <>Pulling <span className="font-semibold text-slate-700 dark:text-slate-200">{courseTitle}</span> from your workspace.</>
                  : 'Pulling the latest syllabus, topics, and sub-topic content from your workspace.'}
              </p>
            </div>
          </div>
        )}

        {!isLoadingSyllabus && !subjectId && (
          <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 text-center">
            <div className="h-14 w-14 rounded-2xl bg-slate-100 dark:bg-slate-800/60 flex items-center justify-center">
              <FileText className="h-6 w-6 text-slate-400" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-bold text-slate-900 dark:text-white">No subject selected</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm">
                Pick a subject from your lesson plans to start editing its syllabus.
              </p>
            </div>
            <Button
              onClick={() => navigate('/dashboard/lessons', { replace: true })}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 rounded-xl h-10"
            >
              Go to lesson plans
            </Button>
          </div>
        )}

        {!isLoadingSyllabus && subjectId && (
        <>
        {/* SECTION 1: COURSE OUTLINE VIEWER/OPENER */}
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 px-1">
            <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-indigo-500" /> Pedagogical Outline Draft
            </h2>
            {outlineContent && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard/lessons/course/outline', { state: { subjectId, subjectName: courseTitle, isFirebaseSubject } })}
                className="text-[10px] font-black uppercase tracking-widest text-indigo-500 hover:text-indigo-600 p-0 h-auto"
              >
                <Edit3 className="h-3 w-3 mr-1" /> Edit Full Outline
              </Button>
            )}
          </div>

          <Card className="border border-slate-200 dark:border-slate-800 shadow-lg bg-white dark:bg-slate-950 overflow-hidden group">
            <CardContent className="p-0">
              {!outlineContent ? (
                <div className="p-6 sm:p-12 flex flex-col items-center justify-center text-center space-y-4">
                   <div className="h-16 w-16 rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 flex items-center justify-center mb-2">
                      <FileText className="h-8 w-8 text-indigo-500" />
                   </div>
                   <div>
                     <h3 className="text-lg font-bold text-slate-900 dark:text-white">Draft Course Philosophy</h3>
                     <p className="text-sm text-slate-500 max-w-sm mx-auto mt-1">Initialize your professional course overview, objectives, and pedagogical strategy.</p>
                   </div>
                   <Button 
                     onClick={() => navigate('/dashboard/lessons/course/outline', { state: { subjectId, subjectName: courseTitle, isFirebaseSubject } })}
                     className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-8 rounded-xl h-11 flex items-center gap-2 transition-all"
                   >
                     <Plus className="h-4 w-4" /> Start Drafting Outline
                   </Button>
                </div>
              ) : (
                <div className="relative">
                  <div 
                    className={cn(
                      "p-4 sm:p-8 prose prose-slate dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 transition-all duration-500 overflow-hidden",
                      !showFullOutline ? "max-h-[300px] mask-gradient-b" : "max-h-none"
                    )}
                    dangerouslySetInnerHTML={{ __html: outlineContent }}
                  />
                   
                  <div className={cn(
                    "absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex justify-center bg-gradient-to-t from-white dark:from-slate-950 to-transparent transition-opacity",
                    showFullOutline ? "opacity-0 pointer-events-none" : "opacity-100"
                  )}>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowFullOutline(true)}
                      className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-slate-200 dark:border-slate-700 font-bold rounded-full h-10 px-6 shadow-md"
                    >
                      <ChevronDown className="h-4 w-4 mr-2" /> Expand Preview
                    </Button>
                  </div>

                  {showFullOutline && (
                    <div className="p-6 pt-0 flex justify-center border-t border-slate-100 dark:border-slate-800/50 mt-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowFullOutline(false)}
                        className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-indigo-500"
                      >
                        <ChevronUp className="h-4 w-4 mr-2" /> Collapse Preview
                      </Button>
                    </div>
                  )}
                  
                  {/* Floating Action Button for Editing when expanded */}
                  <Button 
                    variant="secondary"
                    onClick={() => navigate('/dashboard/lessons/course/outline', { state: { subjectId, subjectName: courseTitle } })}
                    className="absolute top-6 right-6 h-10 w-10 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-xl opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center p-0"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Saved Reference PDF */}
          {mainPdfUrl && (
            <Card className="border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/30 dark:bg-emerald-950/20 overflow-hidden">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center shadow-md shrink-0">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Reference PDF Saved</h4>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Click to view your uploaded course reference</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate('/dashboard/lessons/course/content', {
                    state: {
                      directPdfUrl: mainPdfUrl,
                      htmlContent: outlineContent || null,
                      fromPath: '/dashboard/lessons/course/structure',
                      subjectId,
                      subjectName: courseTitle,
                      isFirebaseSubject,
                      contentTitle: courseTitle
                    }
                  })}
                  className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] uppercase tracking-widest px-4 shadow-md transition-all active:scale-95 w-full sm:w-auto"
                >
                  <Eye className="h-3 w-3 mr-2" /> View PDF
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* SECTION 2: TOPICS & SUBTOPICS */}
        <section className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-2 px-1">
            <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
              <LayoutList className="h-3.5 w-3.5 text-emerald-500" /> Syllabus Breakdown
            </h2>
            <div className="flex items-center gap-3">
               <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700">
                 {topics.length} Main Topics
               </span>
            </div>
          </div>

          <div className="space-y-4">
            {/* New Topic Input */}
            <div className="relative group">
              <Input 
                placeholder="Declare a new primary topic..." 
                className="h-10 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:border-indigo-500 rounded-lg pl-4 pr-36 sm:pr-32 text-sm transition-all"
                value={newTopicTitle}
                onChange={(e) => setNewTopicTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTopic()}
              />
              <div className="absolute right-1.5 top-1.5">
                <Button 
                  onClick={addTopic} 
                  className="h-7 px-3 sm:px-5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white font-black text-[9px] uppercase tracking-[0.1em] shadow-md shadow-indigo-500/10 transition-all hover:scale-[1.02] active:scale-95 border-none whitespace-nowrap"
                >
                   <PlusCircle className="h-3 w-3 mr-1 sm:mr-2 group-hover:rotate-90 transition-transform duration-300" /> <span className="hidden sm:inline">Add Main Topic</span><span className="sm:hidden">Add</span>
                </Button>
              </div>
            </div>

            {/* Topics List */}
            <div className="space-y-4">
              {topics.map((topic, index) => {
                const topicMainKey = `${topic.id}_main`;
                const topicHasContent = subContentMap[topicMainKey];
                const topicHasSavedContent = topicHasContent?.hasPdf || topicHasContent?.hasOutline;
                
                return (
                <Card key={topic.id} className="border border-slate-200 dark:border-slate-800 shadow-sm group/card bg-white dark:bg-slate-950 transition-all hover:shadow-indigo-500/5 overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-3 p-4 cursor-pointer" onClick={() => toggleTopic(topic.id)}>
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 min-w-0 flex-1">
                       <GripVertical className="h-4 w-4 text-slate-200 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                       <div className="flex flex-wrap items-center gap-3 min-w-0">
                          <div className="relative">
                            <div className="h-6 w-6 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-500 shadow-inner">
                              {index + 1}
                            </div>
                            <div className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full bg-indigo-500 border border-white dark:border-slate-950" />
                          </div>
                          {editingTopicId === topic.id ? (
                            <Input
                              autoFocus
                              value={editingTopicValue}
                              onChange={(e) => setEditingTopicValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveTopicRename(topic.id, editingTopicValue);
                                if (e.key === 'Escape') setEditingTopicId(null);
                              }}
                              onBlur={() => saveTopicRename(topic.id, editingTopicValue)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-7 text-sm font-bold border-indigo-400 focus-visible:ring-indigo-400/20 px-2 rounded-lg bg-white dark:bg-slate-900 w-56"
                            />
                          ) : (
                            <div className="flex items-center gap-1 group/title">
                              <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200 tracking-tight">{topic.title}</h3>
                              <button
                                title="Rename topic"
                                onClick={(e) => { e.stopPropagation(); setEditingTopicId(topic.id); setEditingTopicValue(topic.title); }}
                                className="opacity-0 group-hover/title:opacity-100 transition-opacity p-0.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-slate-400 hover:text-indigo-500"
                              >
                                <Edit3 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                       </div>
                    </div>
                    <div className="flex items-center gap-2">
                       <Button 
                         variant="ghost" 
                         size="icon" 
                         onClick={(e) => { e.stopPropagation(); openSubTopicDialog(topic.id); }}
                         className="h-8 w-8 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-emerald-600 transition-all"
                       >
                         <PlusCircle className="h-4 w-4" />
                       </Button>
                       <Button 
                         variant="ghost" 
                         size="icon" 
                         onClick={(e) => { e.stopPropagation(); removeTopic(topic.id); }}
                         className="h-8 w-8 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-500 transition-all"
                       >
                         <Trash2 className="h-4 w-4" />
                       </Button>
                       <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />
                       <div className="h-8 w-8 rounded-lg bg-slate-50 dark:bg-slate-900 flex items-center justify-center text-slate-400 group-hover/card:text-indigo-500 transition-colors shadow-sm">
                        {topic.isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                       </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {topic.isExpanded && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden bg-slate-50/30 dark:bg-[#04091a]/30"
                      >
                        <div className="p-4 pt-0 pl-2 sm:pl-16 space-y-2 pb-6">
                           {topic.subTopics.length === 0 ? (
                             <div className="flex flex-col items-center justify-center py-6 bg-white/50 dark:bg-slate-900/50 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                                <PlusCircle className="h-6 w-6 text-slate-200 mb-2" />
                                <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400">Empty Syllabus Section</p>
                                <div className="flex flex-col items-center gap-3 mt-4">
                                   <Button 
                                     variant="ghost" 
                                     onClick={(e) => { e.stopPropagation(); openSubTopicDialog(topic.id); }}
                                     className="text-indigo-500 font-bold text-[10px] uppercase tracking-widest h-auto p-0 hover:bg-transparent"
                                   >
                                     Add Sub-Topic
                                   </Button>
                                   <Button 
                                       onClick={(e) => { 
                                         e.stopPropagation(); 
                                         const contentKey = `${topic.id}_main`;
                                         const hasContent = (subContentMap as any)[contentKey];
                                         if (hasContent?.referencePdfUrl || hasContent?.outlineContent) {
                                           navigate('/dashboard/lessons/course/content', { 
                                             state: { 
                                               directPdfUrl: hasContent?.referencePdfUrl || null,
                                               htmlContent: hasContent?.outlineContent || null,
                                               fromPath: '/dashboard/lessons/course/structure',
                                               subjectId,
                                               subjectName: courseTitle,
                                               isFirebaseSubject,
                                               contentTitle: topic.title
                                             } 
                                           });
                                         } else {
                                            navigate('/dashboard/lessons/course/outline', {
                                              state: {
                                                subjectId,
                                                subjectName: courseTitle,
                                                isFirebaseSubject,
                                                docType: 'subtopic',
                                                topicId: topic.id,
                                                subTopicId: 'main',
                                                subTopicTitle: topic.title,
                                                returnPath: '/dashboard/lessons/course/structure'
                                              }
                                            });
                                         }
                                       }}
                                       className={cn("h-9 rounded-xl font-bold text-[10px] uppercase tracking-widest px-6 flex items-center gap-2 shadow-md transition-all active:scale-95", topicHasSavedContent ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/10" : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-500/10")}
                                    >
                                      {topicHasSavedContent ? <><Eye className="h-4 w-4" /> View Topic Content</> : <><Wind className="h-4 w-4" /> Add Topic Content</>}
                                    </Button>
                                </div>
                             </div>
                           ) : (
                              topic.subTopics.map((st, sIndex) => {
                                const contentKey = `${topic.id}_${st.id}`;
                                const hasContent = subContentMap[contentKey] || (sIndex === 0 ? subContentMap[`${topic.id}_main`] : null);
                                const hasSavedContent = hasContent?.hasPdf || hasContent?.hasOutline;
                                return (
                                <div
                                  key={st.id}
                                   onClick={() => {
                                     const contentKey = `${topic.id}_${st.id}`;
                                     const hasContent = (subContentMap as any)[contentKey] || (sIndex === 0 ? (subContentMap as any)[`${topic.id}_main`] : null);
                                     if (hasContent?.referencePdfUrl || hasContent?.outlineContent || (hasContent?.itemsCount ?? 0) > 0) {
                                       navigate('/dashboard/lessons/course/content', {
                                         state: {
                                           directPdfUrl: hasContent?.referencePdfUrl || null,
                                           htmlContent: hasContent?.outlineContent || null,
                                           items: hasContent?.items || null,
                                           fromPath: '/dashboard/lessons/course/structure',
                                           subjectId,
                                           subjectName: courseTitle,
                                           isFirebaseSubject,
                                           contentTitle: st.title,
                                           docType: 'subtopic',
                                           topicId: topic.id,
                                           subTopicId: st.id,
                                           subTopicTitle: st.title
                                         }
                                       });
                                     } else {
                                        navigate('/dashboard/lessons/course/outline', {
                                          state: {
                                            subjectId,
                                            subjectName: courseTitle,
                                            isFirebaseSubject,
                                            docType: 'subtopic',
                                            topicId: topic.id,
                                            subTopicId: st.id,
                                            subTopicTitle: st.title,
                                            returnPath: '/dashboard/lessons/course/structure'
                                          }
                                        });
                                     }
                                   }}
                                   className={cn("flex flex-wrap items-center justify-between gap-2 sm:gap-3 py-2.5 px-3 sm:px-4 bg-white dark:bg-slate-900 rounded-xl border shadow-sm group/sub transition-all cursor-pointer", hasSavedContent ? "border-emerald-200 dark:border-emerald-900/50 hover:border-emerald-300" : "border-slate-100 dark:border-slate-800/50 hover:border-indigo-200 dark:hover:border-indigo-900/50")}
                                 >
                                   <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                      <div className={cn("h-1.5 w-1.5 rounded-full transition-colors", hasSavedContent ? "bg-emerald-500" : "bg-slate-200 dark:bg-slate-700 group-hover/sub:bg-indigo-500")} />
                                      <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter leading-none mb-0.5">{index + 1}.{sIndex + 1} Module</span>
                                        <div className="flex items-center gap-2">
                                          {editingSubKey === `${topic.id}_${st.id}` ? (
                                            <Input
                                              autoFocus
                                              value={editingSubValue}
                                              onChange={(e) => setEditingSubValue(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveSubTopicRename(topic.id, st.id, editingSubValue);
                                                if (e.key === 'Escape') setEditingSubKey(null);
                                              }}
                                              onBlur={() => saveSubTopicRename(topic.id, st.id, editingSubValue)}
                                              onClick={(e) => e.stopPropagation()}
                                              className="h-6 text-sm border-indigo-400 focus-visible:ring-indigo-400/20 px-2 rounded-md bg-white dark:bg-slate-900 w-44"
                                            />
                                          ) : (
                                            <div className="flex items-center gap-1 group/stitle">
                                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{st.title}</span>
                                              <button
                                                title="Rename sub-topic"
                                                onClick={(e) => { e.stopPropagation(); setEditingSubKey(`${topic.id}_${st.id}`); setEditingSubValue(st.title); }}
                                                className="opacity-0 group-hover/stitle:opacity-100 transition-opacity p-0.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 text-slate-400 hover:text-indigo-500"
                                              >
                                                <Edit3 className="h-3 w-3" />
                                              </button>
                                            </div>
                                          )}
                                          {hasSavedContent && (
                                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[8px] font-bold uppercase tracking-wider">
                                              <CheckCircle2 className="h-2.5 w-2.5" />
                                              {hasContent.itemsCount > 0
                                                ? `${hasContent.itemsCount} item${hasContent.itemsCount === 1 ? '' : 's'}`
                                                : (hasContent.hasPdf ? 'PDF' : 'Draft')}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                   </div>
                                   <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                                       <Button
                                         variant="ghost"
                                         size="sm"
                                         onClick={(e) => {
                                           e.stopPropagation();
                                           const contentKey = `${topic.id}_${st.id}`;
                                           const hasContent = (subContentMap as any)[contentKey] || (sIndex === 0 ? (subContentMap as any)[`${topic.id}_main`] : null);
                                           if (hasContent?.referencePdfUrl || hasContent?.outlineContent || (hasContent?.itemsCount ?? 0) > 0) {
                                             navigate('/dashboard/lessons/course/content', {
                                               state: {
                                                 directPdfUrl: hasContent?.referencePdfUrl || null,
                                                 htmlContent: hasContent?.outlineContent || null,
                                                 items: hasContent?.items || null,
                                                 fromPath: '/dashboard/lessons/course/structure',
                                                 subjectId,
                                                 subjectName: courseTitle,
                                                 isFirebaseSubject,
                                                 contentTitle: st.title,
                                                 docType: 'subtopic',
                                                 topicId: topic.id,
                                                 subTopicId: st.id,
                                                 subTopicTitle: st.title
                                               }
                                             });
                                         } else {
                                           navigate('/dashboard/lessons/course/outline', {
                                             state: {
                                               subjectId,
                                               subjectName: courseTitle,
                                               isFirebaseSubject,
                                               docType: 'subtopic',
                                               topicId: topic.id,
                                               subTopicId: st.id,
                                               subTopicTitle: st.title,
                                               returnPath: '/dashboard/lessons/course/structure'
                                             }
                                           });
                                         }
                                       }}
                                       className={cn("h-8 rounded-lg font-bold text-[10px] uppercase tracking-widest px-3 flex items-center gap-2 transition-all hover:scale-[1.05] active:scale-95 border", hasSavedContent ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border-emerald-100/50 dark:border-emerald-800/50 hover:bg-emerald-600 hover:text-white hover:shadow-lg hover:shadow-emerald-500/10" : "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-100/50 dark:border-indigo-800/50 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 dark:hover:text-white hover:shadow-lg hover:shadow-indigo-500/10")}
                                     >
                                       {hasSavedContent ? <><Eye className="h-3 w-3" /> View Content</> : <><Wind className="h-3 w-3" /> Add Content</>}
                                     </Button>
                                     <Button
                                       variant="ghost"
                                       size="icon"
                                       onClick={(e) => { e.stopPropagation(); removeSubTopic(topic.id, st.id); }}
                                       className="h-7 w-7 rounded-lg opacity-0 group-hover/sub:opacity-100 transition-opacity text-slate-300 hover:text-rose-500"
                                     >
                                       <Trash2 className="h-3.5 w-3.5" />
                                     </Button>
                                   </div>
                                </div>
                              );})
                           )}

                           {/* INLINE SUB-TOPIC DRAFTING FIELD */}
                           <AnimatePresence>
                             {activeTopicId === topic.id ? (
                                <motion.div 
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  className="flex flex-wrap items-center gap-2 p-1 bg-white dark:bg-slate-900 rounded-xl border-2 border-indigo-500/30 shadow-lg shadow-indigo-500/5 mt-2"
                                >
                                   <Input 
                                     autoFocus
                                     placeholder="Sub-topic name (e.g. Memory Safety...)"
                                     value={subTopicTitle}
                                     onChange={(e) => setSubTopicTitle(e.target.value)}
                                     onKeyDown={(e) => {
                                       if (e.key === 'Enter') addSubTopic();
                                       if (e.key === 'Escape') setActiveTopicId(null);
                                     }}
                                     className="border-none bg-transparent shadow-none h-10 text-sm font-medium focus-visible:ring-0 grow min-w-[150px]"
                                   />
                                   <div className="flex gap-1 pr-1">
                                     <Button onClick={addSubTopic} disabled={!subTopicTitle.trim()} className="h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[10px] uppercase px-4">Add Item</Button>
                                     <Button variant="ghost" size="icon" onClick={() => setActiveTopicId(null)} className="h-8 w-8 rounded-lg text-slate-400"><X className="h-4 w-4" /></Button>
                                   </div>
                                </motion.div>
                             ) : (
                               <Button 
                                 variant="ghost" 
                                 size="sm" 
                                 onClick={(e) => { e.stopPropagation(); openSubTopicDialog(topic.id); }}
                                 className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-indigo-600 transition-all flex items-center gap-2 h-9 px-4 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/10"
                               >
                                 <Plus className="h-3.5 w-3.5" /> Add Sub-Topic
                               </Button>
                             )}
                           </AnimatePresence>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              )})}
            </div>
          </div>
        </section>

        {/* COURSE MATERIALS UPLOAD SECTION */}
        <section className="space-y-4 mt-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-indigo-900 dark:text-indigo-100 text-lg">Upload Course Materials</h3>
              <p className="text-indigo-600 dark:text-indigo-300 text-sm">Add schemes of work, textbooks, and common handouts to your course.</p>
            </div>
            <Button
              type="button"
              onClick={() => setIsUploadDialogOpen(true)}
              disabled={isUploading || !subjectId}
              className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg w-full sm:w-auto"
            >
              {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {isUploading ? "Uploading..." : "Upload Resource"}
            </Button>
          </div>

          {/* Upload Resource popup — uses PersonalBookUploadForm configured for the per-course resources collection */}
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogContent className="w-[calc(100%-2rem)] max-w-3xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                  <Upload className="h-5 w-5" /> Upload New Resource
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Add a scheme, textbook, or handout to this course. The file will be linked to <span className="font-bold text-indigo-600 dark:text-indigo-400">{courseTitle || 'this course'}</span>.
                </DialogDescription>
              </DialogHeader>
              <PersonalBookUploadForm
                userId={auth.currentUser?.uid || ''}
                subjectId={subjectId || undefined}
                subjectNameFallback={courseTitle}
                collectionPath={subjectId ? `teaching_workspaces/${auth.currentUser?.uid}/courses/${subjectId}/resources` : undefined}
                storagePrefix={subjectId ? `teaching_workspaces/${auth.currentUser?.uid}/courses/${subjectId}/resources/` : undefined}
                submitLabel="Upload to Course"
                successMessage="Resource added to this course"
                onClose={() => setIsUploadDialogOpen(false)}
                onSubmit={async () => {
                  if (!auth.currentUser || !subjectId) return;
                  const courseRef = doc(db, `teaching_workspaces/${auth.currentUser.uid}/courses`, subjectId);
                  await updateDoc(courseRef, { resourcesUpdatedAt: serverTimestamp() });
                }}
              />
            </DialogContent>
          </Dialog>
          {subjectResources.length > 0 && (
            <div className="space-y-2">
              {subjectResources.map((file, i) => {
                const fileName = file.title || file.name || 'Untitled';
                const fileUrl = file.pdfUrl || file.url || '';
                const fileType = file.fileType || file.type || file.mime || '';
                const sizeMB = file.fileSize
                  ? parseFloat(String(file.fileSize))
                  : (typeof file.size === 'number' ? file.size / 1024 / 1024 : 0);
                return (
                  <div key={file.id || i} className="flex items-center justify-between p-4 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/5 rounded-xl hover:bg-gray-50 dark:hover:bg-white/10 transition-colors group">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center relative overflow-hidden shrink-0">
                        {String(fileType).toLowerCase().includes('image') ? (
                          <img src={fileUrl} alt={fileName} className="object-cover w-full h-full" />
                        ) : (
                          <FileText className="h-5 w-5" />
                        )}
                      </div>
                      <div className="overflow-hidden">
                        <h4 className="font-bold text-sm max-w-[200px] md:max-w-md truncate">{fileName}</h4>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                          {sizeMB ? `${sizeMB.toFixed(2)} MB • ` : ''}{file.createdAt?.toDate ? new Date(file.createdAt.toDate()).toLocaleDateString() : file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : 'Just now'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="icon" variant="ghost" onClick={() => handleDownloadResource(fileUrl, fileName)}>
                        <Download className="h-4 w-4 text-indigo-500" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDeleteResource(file.id)} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* EDIT SUBJECT DETAILS DIALOG */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) { setAddingClassForSchool(null); setNewClassName(''); } }}>
          <DialogContent className="w-[calc(100%-2rem)] max-w-2xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2 text-indigo-500">
                <Settings className="h-5 w-5" /> Edit Subject Details
              </DialogTitle>
              <DialogDescription className="text-sm">Update the core properties of this subject.</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="edit-title" className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                    <BookOpen className="h-3 w-3" /> Subject/Course Title
                  </Label>
                  <Input 
                    id="edit-title"
                    placeholder="e.g. Mathematics, Science"
                    className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm"
                    value={editFormData.title}
                    onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                  />
                </div>

                {/* Syllabus Hours only — no Levels */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="edit-approxHours" className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                    <Clock className="h-3 w-3" /> Syllabus Hours
                  </Label>
                  <Input 
                    id="edit-approxHours"
                    type="number"
                    placeholder="e.g. 40"
                    className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm"
                    value={editFormData.approxHours}
                    onChange={(e) => setEditFormData({...editFormData, approxHours: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                    <Tag className="h-3 w-3" /> Category
                  </Label>
                  <Popover open={openCategory} onOpenChange={setOpenCategory}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openCategory}
                        className="w-full justify-between bg-white dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 rounded-lg text-sm font-normal text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900/80"
                      >
                        {editFormData.category ? (
                          <div className="flex items-center gap-2">
                            {(() => {
                              const cat = subjectCategories.find(c => c.value === editFormData.category);
                              if (cat) {
                                const Icon = cat.icon;
                                return (
                                  <div className={cn("p-1.5 rounded-md flex items-center justify-center border", cat.bg, cat.border)}>
                                    <Icon className={cn("h-4 w-4", cat.color)} />
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            <span className="text-slate-900 dark:text-slate-100 font-bold ml-1">
                              {editFormData.category === 'other' && editFormData.customCategory 
                                ? editFormData.customCategory 
                                : subjectCategories.find((c) => c.value === editFormData.category)?.label}
                            </span>
                          </div>
                        ) : (
                          "Select category..."
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl rounded-xl overflow-hidden">
                      <Command className="bg-transparent">
                        <CommandInput 
                          placeholder="Search categories..." 
                          className="h-10 border-none focus:ring-0 text-sm text-slate-900 dark:text-slate-100" 
                        />
                        <CommandList className="max-h-[300px] overflow-y-auto">
                          <CommandEmpty className="py-4 text-xs text-muted-foreground uppercase font-bold tracking-tighter italic text-center">No category found</CommandEmpty>
                          <CommandGroup className="p-1">
                            {subjectCategories.map((category) => (
                              <CommandItem
                                key={category.value}
                                value={category.value}
                                onSelect={(currentValue) => {
                                  setEditFormData({...editFormData, category: currentValue})
                                  setOpenCategory(false)
                                }}
                                className="flex items-center gap-3 py-2.5 px-3 mb-1 rounded-lg cursor-pointer data-[selected=true]:bg-slate-50 dark:data-[selected=true]:bg-slate-800/50 text-slate-700 dark:text-slate-300 text-sm transition-all group/item"
                              >
                                <div className={cn(
                                  "p-2 rounded-lg flex items-center justify-center border shadow-sm transition-all group-data-[selected=true]/item:scale-110",
                                  category.bg,
                                  category.border
                                )}>
                                  <category.icon className={cn("h-4 w-4", category.color)} />
                                </div>
                                <span className="flex-1 font-bold tracking-tight">{category.label}</span>
                                <Check
                                  className={cn(
                                    "h-4 w-4 text-indigo-500 transition-opacity",
                                    editFormData.category === category.value ? "opacity-100" : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {editFormData.category === 'other' && (
                    <div className="mt-2 space-y-2">
                      <Label htmlFor="edit-customCategory" className="text-[10px] font-bold uppercase text-slate-500">
                        Custom Category Name
                      </Label>
                      <Input 
                        id="edit-customCategory"
                        placeholder="Enter subject type..."
                        className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm h-10"
                        value={editFormData.customCategory}
                        onChange={(e) => setEditFormData({...editFormData, customCategory: e.target.value})}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                    <GlobeIcon className="h-3 w-3" /> Multi-School
                  </Label>
                  <Select 
                    value={editFormData.isMultiSchool} 
                    onValueChange={(v) => setEditFormData({...editFormData, isMultiSchool: v})}
                  >
                    <SelectTrigger className="bg-white dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 rounded-lg text-sm transition-all focus:ring-2 focus:ring-indigo-500">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 shadow-xl rounded-xl">
                      <SelectItem value="no">No — single school</SelectItem>
                      <SelectItem value="yes">Yes — multiple schools</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4 md:col-span-2">
                  <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200/50 dark:border-slate-800/50">
                    <Checkbox 
                      id="edit-continuation" 
                      checked={editFormData.isContinuation}
                      onCheckedChange={(checked) => setEditFormData({...editFormData, isContinuation: !!checked})}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <label htmlFor="edit-continuation" className="text-xs font-bold leading-none flex items-center gap-2">
                        <RefreshCcw className="h-3 w-3 text-slate-500" /> Subject Continuation
                      </label>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-tight">Recurring curriculum</p>
                    </div>
                  </div>

                  {editFormData.isContinuation && (
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Continues From</Label>
                      <Popover open={openSearch} onOpenChange={setOpenSearch}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="w-full justify-between bg-white dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 rounded-lg text-sm font-normal text-slate-600 dark:text-slate-400"
                          >
                            {editFormData.continuesFrom
                              ? existingSubjects.find((s) => s.value === editFormData.continuesFrom)?.label
                              : "Select preceding subject..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl rounded-xl overflow-hidden">
                          <Command>
                            <CommandInput placeholder="Search subjects..." className="h-10 border-none focus:ring-0" />
                            <CommandList className="max-h-[200px] overflow-y-auto">
                              <CommandEmpty className="py-4 text-xs text-center font-bold text-slate-400 uppercase">No subjects found</CommandEmpty>
                              <CommandGroup>
                                {existingSubjects.filter(s => s.value !== subjectId).map((s) => (
                                  <CommandItem
                                    key={s.value}
                                    value={s.value}
                                    onSelect={(currentValue) => {
                                      setEditFormData({...editFormData, continuesFrom: currentValue === editFormData.continuesFrom ? "" : currentValue})
                                      setOpenSearch(false)
                                    }}
                                    className="flex items-center justify-between py-2 text-sm"
                                  >
                                    <span>{s.label}</span>
                                    <Check className={cn("h-4 w-4 text-indigo-500", editFormData.continuesFrom === s.value ? "opacity-100" : "opacity-0")} />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}

                  {/* ── Schools + per-school Classes ── */}
                  {editFormData.isMultiSchool === 'yes' && (
                    <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800/50">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                          <Building2 className="h-3 w-3" /> Schools &amp; Classes
                        </Label>
                        <span className="text-[10px] text-slate-400 font-medium">Each school has its own class list</span>
                      </div>

                      <div className="space-y-4">
                        {editFormData.schoolsData.map((school, sIdx) => (
                          <div key={sIdx} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 overflow-hidden">
                            {/* School name row */}
                            <div className="flex items-center gap-2 p-3 border-b border-slate-200 dark:border-slate-800">
                              <div className="flex-1 relative group">
                                <Input
                                  placeholder={`School / University ${sIdx + 1} name`}
                                  className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-lg text-sm pr-9 focus:ring-2 focus:ring-indigo-500"
                                  value={school.name}
                                  onChange={(e) => handleSchoolNameChange(sIdx, e.target.value)}
                                />
                                <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-300 group-focus-within:text-indigo-400 transition-colors pointer-events-none" />
                              </div>
                              {editFormData.schoolsData.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveSchoolEdit(sIdx)}
                                  className="h-9 w-9 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 shrink-0"
                                >
                                  <Scissors className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>

                            {/* Classes for this school */}
                            <div className="p-3 space-y-2">
                              {school.classes.filter(c => c.trim() !== '').length === 0 ? (
                                <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">No classes yet</p>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {school.classes.filter(c => c.trim() !== '').map((cls, cIdx) => (
                                    <span
                                      key={cIdx}
                                      className="inline-flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-lg px-3 py-1 text-xs font-bold"
                                    >
                                      {cls}
                                      <button
                                        type="button"
                                        aria-label={`Remove class ${cls}`}
                                        onClick={() => handleRemoveClassFromSchool(sIdx, cIdx)}
                                        className="ml-0.5 hover:text-rose-500 transition-colors"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Inline add class */}
                              {addingClassForSchool === sIdx ? (
                                <div className="flex flex-wrap items-center gap-2 pt-1">
                                  <Input
                                    autoFocus
                                    placeholder="Class name (e.g. Form 3 East)"
                                    className="h-8 flex-1 min-w-[140px] rounded-lg border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs focus:ring-2 focus:ring-indigo-500"
                                    value={newClassName}
                                    onChange={(e) => setNewClassName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); handleAddClassToSchool(sIdx); }
                                      if (e.key === 'Escape') { setAddingClassForSchool(null); setNewClassName(''); }
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => handleAddClassToSchool(sIdx)}
                                    disabled={!newClassName.trim()}
                                    className="h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black px-3 shrink-0"
                                  >
                                    Add
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setAddingClassForSchool(null); setNewClassName(''); }}
                                    className="h-8 rounded-lg text-[10px] text-slate-400 hover:text-slate-600 shrink-0"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => { setAddingClassForSchool(sIdx); setNewClassName(''); }}
                                  className="mt-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                                >
                                  <Plus className="h-3 w-3" /> Add Class
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={handleAddSchoolEdit}
                        className="w-full mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl py-3 text-[10px] font-black uppercase tracking-wider text-slate-400 hover:text-indigo-500 hover:border-indigo-400 transition-all"
                      >
                        <Building2 className="h-3.5 w-3.5" /> Add Another School
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="pt-6 border-t border-slate-100 dark:border-slate-800/50 mt-6">
              <Button variant="ghost" onClick={() => setIsEditDialogOpen(false)} className="rounded-lg px-4 text-xs font-bold text-slate-500">Cancel</Button>
              <Button 
                onClick={handleSaveSubjectDetails} 
                disabled={isSavingDetails}
                className="rounded-lg px-6 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 font-bold transition-all shadow-lg flex items-center gap-2"
              >
                {isSavingDetails ? <Sparkles className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Update Subject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DELETE SUBJECT CONFIRMATION */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent className="w-[calc(100%-2rem)] bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-slate-900 dark:text-white">Delete this subject?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                This will permanently delete the subject, all its syllabus topics, sub-topics, uploaded resources, notes, bookmarks, and reading progress. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteSubject}
                disabled={isDeletingSubject}
                className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
              >
                {isDeletingSubject ? "Deleting..." : "Delete Subject"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </>
        )}
      </div>
    </div>
  );
};

export default CourseOutlineEditor;
